import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useSearchParams } from 'react-router-dom';
import { 
  Store, MapPin, Phone, Mail, Clock, Truck, DollarSign, Save, Image, Package, UtensilsCrossed, Plus, X, CheckCircle, Coins, Crosshair, Navigation, AlertCircle, CreditCard, ExternalLink, Loader2, XCircle, Monitor, Wifi, Server, Percent
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ImageUpload } from '../../components/ImageUpload';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { toast } from 'sonner';

export default function RestaurantSettings() {
  const { api } = useAuth();
  // Use shared restaurant context
  const { restaurants, selectedRestaurant, setSelectedRestaurant, refreshRestaurants } = useRestaurant();
  const { CURRENCIES } = useCurrency();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: '',
    cuisine_type: '',
    latitude: null,
    longitude: null,
    currency: 'GBP',
    delivery_enabled: true,
    takeaway_enabled: true,
    table_booking_enabled: false,
    delivery_radius_km: 10,
    minimum_order: 10,
    delivery_fee: 2.50,
    image_url: '',
    // Price adjustments by order type (percentage)
    price_adjustment_delivery: 0,
    price_adjustment_takeaway: 0,
    opening_hours: {
      monday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
      tuesday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
      wednesday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
      thursday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
      friday: { slots: [{ open: '09:00', close: '23:00' }], closed: false },
      saturday: { slots: [{ open: '10:00', close: '23:00' }], closed: false },
      sunday: { slots: [{ open: '10:00', close: '22:00' }], closed: false },
    }
  });

  // Map preview state
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  // Geolocation state
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Initialize map preview when coordinates change
  const initMapPreview = useCallback(() => {
    if (!mapRef.current || !window.google?.maps) return;
    
    const lat = formData.latitude;
    const lng = formData.longitude;
    
    if (!lat || !lng) {
      // Hide map if no coordinates
      if (mapInstanceRef.current) {
        mapRef.current.style.display = 'none';
      }
      return;
    }

    // Show map container
    mapRef.current.style.display = 'block';

    const position = { lat: parseFloat(lat), lng: parseFloat(lng) };

    if (!mapInstanceRef.current) {
      // Create new map instance
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: position,
        zoom: 16,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        zoomControl: true,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
        ]
      });

      // Create marker
      markerRef.current = new window.google.maps.Marker({
        position: position,
        map: mapInstanceRef.current,
        title: formData.name || 'Your Restaurant',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#f97316',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3
        }
      });
    } else {
      // Update existing map and marker
      mapInstanceRef.current.setCenter(position);
      markerRef.current.setPosition(position);
    }

    setMapLoaded(true);
  }, [formData.latitude, formData.longitude, formData.name]);

  // Load Google Maps script if needed and initialize preview
  useEffect(() => {
    const initMap = () => {
      if (window.google?.maps) {
        initMapPreview();
      }
    };

    // Check if script already loaded
    if (window.google?.maps) {
      initMap();
      return;
    }

    // Wait for script to load (it's loaded by AddressAutocomplete)
    const checkInterval = setInterval(() => {
      if (window.google?.maps) {
        clearInterval(checkInterval);
        initMap();
      }
    }, 500);

    return () => clearInterval(checkInterval);
  }, [initMapPreview]);

  // Convert old format to new format with slots
  const convertOpeningHours = (hours) => {
    const defaultHours = {
      monday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
      tuesday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
      wednesday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
      thursday: { slots: [{ open: '09:00', close: '22:00' }], closed: false },
      friday: { slots: [{ open: '09:00', close: '23:00' }], closed: false },
      saturday: { slots: [{ open: '10:00', close: '23:00' }], closed: false },
      sunday: { slots: [{ open: '10:00', close: '22:00' }], closed: false },
    };
    
    if (!hours || typeof hours !== 'object') return defaultHours;
    
    const converted = {};
    for (const day of days) {
      if (hours[day]) {
        if (hours[day].slots && Array.isArray(hours[day].slots)) {
          // Already in new format
          converted[day] = hours[day];
        } else if (hours[day].open && hours[day].close) {
          // Old format - convert to slots
          converted[day] = { slots: [{ open: hours[day].open, close: hours[day].close }], closed: false };
        } else {
          converted[day] = defaultHours[day];
        }
      } else {
        converted[day] = defaultHours[day];
      }
    }
    return converted;
  };

  // Track if form has been initialized
  const [formInitialized, setFormInitialized] = useState(false);

  // Fetch Stripe status when restaurant changes
  const fetchStripeStatus = useCallback(async () => {
    if (!selectedRestaurant?.id) {
      setStripeStatus(null);
      setStripeLoading(false);
      return;
    }
    
    setStripeLoading(true);
    try {
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/stripe-connect/status`);
      setStripeStatus(response.data);
    } catch (error) {
      console.log('Stripe status not available');
      setStripeStatus(null);
    } finally {
      setStripeLoading(false);
    }
  }, [api, selectedRestaurant?.id]);

  useEffect(() => {
    if (selectedRestaurant?.id) {
      fetchStripeStatus();
    }
    
    // Handle Stripe return
    if (searchParams.get('stripe_success') === 'true') {
      toast.success('Stripe onboarding completed! Your account is being verified.');
      fetchStripeStatus();
    } else if (searchParams.get('stripe_refresh') === 'true') {
      toast.info('Please complete your Stripe onboarding.');
    }
  }, [selectedRestaurant?.id, searchParams, fetchStripeStatus]);

  // Stripe Connect handlers
  const handleStripeOnboard = async () => {
    if (!selectedRestaurant?.id) return;
    
    setOnboardingLoading(true);
    try {
      const response = await api.post(`/restaurants/${selectedRestaurant.id}/stripe-connect/onboard`);
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Stripe onboarding');
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handleStripeDashboard = async () => {
    if (!selectedRestaurant?.id) return;
    
    try {
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/stripe-connect/dashboard`);
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  useEffect(() => {
    // Only set form data on initial load, not on subsequent restaurant updates
    if (selectedRestaurant && !formInitialized) {
      setFormData({
        name: selectedRestaurant.name || '',
        description: selectedRestaurant.description || '',
        address: selectedRestaurant.address || '',
        city: selectedRestaurant.city || '',
        postal_code: selectedRestaurant.postal_code || '',
        phone: selectedRestaurant.phone || '',
        email: selectedRestaurant.email || '',
        cuisine_type: (selectedRestaurant.cuisine_type || []).join(', '),
        latitude: selectedRestaurant.latitude || null,
        longitude: selectedRestaurant.longitude || null,
        currency: selectedRestaurant.currency || 'GBP',
        delivery_enabled: selectedRestaurant.delivery_enabled !== false,
        takeaway_enabled: selectedRestaurant.takeaway_enabled !== false,
        table_booking_enabled: selectedRestaurant.table_booking_enabled === true,
        delivery_radius_km: selectedRestaurant.delivery_radius_km || 10,
        minimum_order: selectedRestaurant.minimum_order || 10,
        delivery_fee: selectedRestaurant.delivery_fee || 2.50,
        image_url: selectedRestaurant.image_url || '',
        // Price adjustments by order type
        price_adjustment_delivery: selectedRestaurant.price_adjustment_delivery || 0,
        price_adjustment_takeaway: selectedRestaurant.price_adjustment_takeaway || 0,
        opening_hours: convertOpeningHours(selectedRestaurant.opening_hours) || formData.opening_hours,
      });
      setFormInitialized(true);
    }
  }, [selectedRestaurant, formInitialized]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle manual coordinate input
  const handleCoordinateChange = (field, value) => {
    const numValue = value === '' ? null : parseFloat(value);
    setFormData(prev => ({
      ...prev,
      [field]: numValue
    }));
    setLocationError(null);
  };

  // Detect current location using browser Geolocation API
  const detectCurrentLocation = () => {
    setDetectingLocation(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setDetectingLocation(false);
      toast.error('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setFormData(prev => ({
          ...prev,
          latitude: latitude,
          longitude: longitude
        }));
        setDetectingLocation(false);
        toast.success('Location detected successfully!');
      },
      (error) => {
        setDetectingLocation(false);
        let errorMessage = 'Failed to detect location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable. Please try again or enter coordinates manually.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage = 'An error occurred while detecting location.';
        }
        setLocationError(errorMessage);
        toast.error(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Clear coordinates
  const clearCoordinates = () => {
    setFormData(prev => ({
      ...prev,
      latitude: null,
      longitude: null
    }));
    setLocationError(null);
    toast.info('Coordinates cleared');
  };

  const handleHoursChange = (day, slotIndex, type, value) => {
    const currentSlots = [...(formData.opening_hours[day]?.slots || [])];
    currentSlots[slotIndex] = { ...currentSlots[slotIndex], [type]: value };
    setFormData({
      ...formData,
      opening_hours: {
        ...formData.opening_hours,
        [day]: {
          ...formData.opening_hours[day],
          slots: currentSlots
        }
      }
    });
  };

  const addTimeSlot = (day) => {
    const currentSlots = [...(formData.opening_hours[day]?.slots || [])];
    currentSlots.push({ open: '16:00', close: '22:00' }); // Default afternoon slot
    setFormData({
      ...formData,
      opening_hours: {
        ...formData.opening_hours,
        [day]: {
          ...formData.opening_hours[day],
          slots: currentSlots
        }
      }
    });
  };

  const removeTimeSlot = (day, slotIndex) => {
    const currentSlots = [...(formData.opening_hours[day]?.slots || [])];
    if (currentSlots.length > 1) {
      currentSlots.splice(slotIndex, 1);
      setFormData({
        ...formData,
        opening_hours: {
          ...formData.opening_hours,
          [day]: {
            ...formData.opening_hours[day],
            slots: currentSlots
          }
        }
      });
    }
  };

  const toggleDayClosed = (day) => {
    setFormData({
      ...formData,
      opening_hours: {
        ...formData.opening_hours,
        [day]: {
          ...formData.opening_hours[day],
          closed: !formData.opening_hours[day]?.closed
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updateData = {
        name: formData.name,
        description: formData.description,
        address: formData.address,
        city: formData.city,
        postal_code: formData.postal_code,
        phone: formData.phone,
        email: formData.email,
        cuisine_type: formData.cuisine_type.split(',').map(s => s.trim()).filter(Boolean),
        currency: formData.currency,
        delivery_enabled: formData.delivery_enabled,
        takeaway_enabled: formData.takeaway_enabled,
        table_booking_enabled: formData.table_booking_enabled,
        delivery_radius_km: parseFloat(formData.delivery_radius_km),
        minimum_order: parseFloat(formData.minimum_order),
        delivery_fee: parseFloat(formData.delivery_fee),
        image_url: formData.image_url,
        opening_hours: formData.opening_hours,
        // Price adjustments by order type
        price_adjustment_delivery: parseFloat(formData.price_adjustment_delivery) || 0,
        price_adjustment_takeaway: parseFloat(formData.price_adjustment_takeaway) || 0,
      };

      // Only include coordinates if they exist
      if (formData.latitude !== null && formData.latitude !== undefined) {
        updateData.latitude = parseFloat(formData.latitude);
      }
      if (formData.longitude !== null && formData.longitude !== undefined) {
        updateData.longitude = parseFloat(formData.longitude);
      }

      console.log('Saving restaurant with data:', updateData);
      
      await api.put(`/restaurants/${selectedRestaurant.id}`, updateData);
      toast.success('Restaurant settings updated');
      
      // Reset form initialized state so fresh data loads after refresh
      setFormInitialized(false);
      refreshRestaurants();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-900">Restaurant Settings</h1>
        {restaurants.length > 1 && (
          <Select 
            value={selectedRestaurant?.id || ''} 
            onValueChange={(value) => setSelectedRestaurant(restaurants.find(r => r.id === value))}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select restaurant" />
            </SelectTrigger>
            <SelectContent>
              {restaurants.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {selectedRestaurant && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5 text-orange-500" />
                Basic Information
              </CardTitle>
              <CardDescription>Update your restaurant's core details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Restaurant Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1"
                    required
                    data-testid="restaurant-name"
                  />
                </div>
                <div>
                  <Label htmlFor="cuisine_type">Cuisine Types (comma separated)</Label>
                  <Input
                    id="cuisine_type"
                    name="cuisine_type"
                    value={formData.cuisine_type}
                    onChange={handleChange}
                    placeholder="Italian, Pizza, Pasta"
                    className="mt-1"
                    data-testid="cuisine-type"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1"
                  placeholder="Describe your restaurant..."
                  data-testid="restaurant-description"
                />
              </div>
              <div>
                <Label>Restaurant Image</Label>
                <div className="mt-2">
                  <ImageUpload
                    endpoint={`/upload/image/restaurant/${selectedRestaurant?.id}`}
                    category="restaurant"
                    currentImage={formData.image_url}
                    onUpload={(url) => setFormData({ ...formData, image_url: url })}
                    placeholder="Upload restaurant image"
                    aspectRatio="aspect-video"
                    className="max-w-md"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Or enter URL manually below
                </p>
              </div>
              <div>
                <Label htmlFor="image_url">Image URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="image_url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="flex-1"
                  />
                  {formData.image_url && (
                    <img 
                      src={formData.image_url.startsWith('http') ? formData.image_url : `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}`} 
                      alt="Restaurant" 
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stripe Connect - Payout Settings */}
          <Card data-testid="stripe-connect-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-orange-500" />
                Payout Settings
              </CardTitle>
              <CardDescription>Connect your Stripe account to receive payments from orders</CardDescription>
            </CardHeader>
            <CardContent>
              {stripeLoading ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Checking Stripe status...</span>
                </div>
              ) : !stripeStatus?.connected ? (
                <div className="bg-amber-50 p-4 rounded-xl" data-testid="stripe-not-connected">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <p className="font-semibold text-amber-700">Stripe Not Connected</p>
                      <p className="text-sm text-amber-600">Connect your Stripe account to receive order payments</p>
                    </div>
                    <Button 
                      onClick={handleStripeOnboard} 
                      className="btn-primary"
                      disabled={onboardingLoading}
                      data-testid="connect-stripe-btn"
                    >
                      {onboardingLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Connect Stripe
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : !stripeStatus.details_submitted ? (
                <div className="bg-orange-50 p-4 rounded-xl" data-testid="stripe-incomplete">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-orange-700">Onboarding Incomplete</p>
                        <p className="text-sm text-orange-600">Please complete your Stripe verification to receive payments</p>
                      </div>
                    </div>
                    <Button 
                      onClick={handleStripeOnboard}
                      variant="outline"
                      className="border-orange-300 text-orange-700 hover:bg-orange-100"
                      disabled={onboardingLoading}
                    >
                      {onboardingLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Complete Onboarding
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4" data-testid="stripe-connected">
                  <div className="bg-green-50 p-4 rounded-xl">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-semibold text-green-700">Stripe Connected</p>
                        <p className="text-sm text-green-600">Your restaurant is ready to receive payments</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        {stripeStatus.payouts_enabled ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                        <span className="text-sm font-medium text-slate-700">Payouts</span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {stripeStatus.payouts_enabled ? 'Enabled' : 'Pending verification'}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        {stripeStatus.charges_enabled ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                        <span className="text-sm font-medium text-slate-700">Charges</span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {stripeStatus.charges_enabled ? 'Enabled' : 'Pending verification'}
                      </p>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleStripeDashboard}
                    variant="outline"
                    className="w-full"
                    data-testid="stripe-dashboard-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Stripe Dashboard
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact & Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-orange-500" />
                Contact & Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="address">Restaurant Address</Label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(value) => setFormData({ ...formData, address: value })}
                  onSelect={(data) => {
                    if (data) {
                      setFormData(prev => ({
                        ...prev,
                        address: data.address,
                        city: data.components?.city || prev.city,
                        postal_code: data.components?.postalCode || prev.postal_code,
                        latitude: data.latitude || prev.latitude,
                        longitude: data.longitude || prev.longitude,
                      }));
                    }
                  }}
                  placeholder="Start typing restaurant address..."
                  className="mt-1"
                  types={['geocode']}
                />
              </div>

              {/* GPS Coordinates Section */}
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Crosshair className="w-4 h-4 text-orange-500" />
                    <span className="font-medium text-sm">GPS Coordinates</span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={detectCurrentLocation}
                    disabled={detectingLocation}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    data-testid="detect-location-btn"
                  >
                    {detectingLocation ? (
                      <>
                        <Navigation className="w-4 h-4 mr-2 animate-pulse" />
                        Detecting...
                      </>
                    ) : (
                      <>
                        <Navigation className="w-4 h-4 mr-2" />
                        Detect Current Location
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-xs text-slate-500 mb-3">
                  Set your restaurant's exact location for delivery calculations. You can detect your current location or enter coordinates manually.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="latitude" className="text-xs">Latitude</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      value={formData.latitude ?? ''}
                      onChange={(e) => handleCoordinateChange('latitude', e.target.value)}
                      placeholder="e.g., 51.5074"
                      className="mt-1 text-sm"
                      data-testid="latitude-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="longitude" className="text-xs">Longitude</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      value={formData.longitude ?? ''}
                      onChange={(e) => handleCoordinateChange('longitude', e.target.value)}
                      placeholder="e.g., -0.1278"
                      className="mt-1 text-sm"
                      data-testid="longitude-input"
                    />
                  </div>
                </div>

                {/* Location Error Message */}
                {locationError && (
                  <div className="mt-3 flex items-start gap-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{locationError}</span>
                  </div>
                )}

                {/* Clear Coordinates Button */}
                {(formData.latitude || formData.longitude) && (
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearCoordinates}
                      className="text-slate-500 hover:text-slate-700"
                      data-testid="clear-coordinates-btn"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Clear Coordinates
                    </Button>
                  </div>
                )}
              </div>

              {/* Map Preview */}
              {(formData.latitude && formData.longitude) && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Location confirmed - coordinates saved</span>
                  </div>
                  <div 
                    ref={mapRef}
                    className="w-full h-48 rounded-lg border border-slate-200 overflow-hidden"
                    style={{ display: formData.latitude ? 'block' : 'none' }}
                    data-testid="location-preview-map"
                  />
                  <p className="text-xs text-slate-500">
                    Lat: {typeof formData.latitude === 'number' ? formData.latitude.toFixed(6) : formData.latitude}, Lng: {typeof formData.longitude === 'number' ? formData.longitude.toFixed(6) : formData.longitude}
                  </p>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="postal_code">Postal Code</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="restaurant-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Service Availability */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5 text-orange-500" />
                Service Availability
              </CardTitle>
              <CardDescription>
                Choose which services your restaurant offers. Customers will only see your restaurant when searching for enabled services.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Delivery Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-medium">Delivery</p>
                    <p className="text-sm text-slate-500">Deliver orders to customers</p>
                  </div>
                </div>
                <Switch
                  checked={formData.delivery_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, delivery_enabled: checked })}
                  data-testid="delivery-toggle"
                />
              </div>

              {/* Takeaway Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-medium">Takeaway</p>
                    <p className="text-sm text-slate-500">Customers collect from restaurant</p>
                  </div>
                </div>
                <Switch
                  checked={formData.takeaway_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, takeaway_enabled: checked })}
                  data-testid="takeaway-toggle"
                />
              </div>

              {/* Table Booking Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <UtensilsCrossed className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="font-medium">Table Booking</p>
                    <p className="text-sm text-slate-500">Allow dine-in reservations with pre-orders</p>
                  </div>
                </div>
                <Switch
                  checked={formData.table_booking_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, table_booking_enabled: checked })}
                  data-testid="table-booking-toggle"
                />
              </div>

              {!formData.delivery_enabled && !formData.takeaway_enabled && !formData.table_booking_enabled && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800 text-sm">
                    ⚠️ At least one service must be enabled for customers to order from your restaurant.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Currency Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-orange-500" />
                Currency
              </CardTitle>
              <CardDescription>Set your restaurant's pricing currency</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <label 
                    className={`flex items-center gap-4 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      formData.currency === 'GBP' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="currency"
                      value="GBP"
                      checked={formData.currency === 'GBP'}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="sr-only"
                    />
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-2xl font-bold text-blue-600">£</span>
                    </div>
                    <div>
                      <p className="font-semibold">British Pound (GBP)</p>
                      <p className="text-sm text-slate-500">United Kingdom</p>
                    </div>
                    {formData.currency === 'GBP' && (
                      <CheckCircle className="w-5 h-5 text-orange-500 ml-auto" />
                    )}
                  </label>
                  
                  <label 
                    className={`flex items-center gap-4 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      formData.currency === 'EUR' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="currency"
                      value="EUR"
                      checked={formData.currency === 'EUR'}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="sr-only"
                    />
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-2xl font-bold text-emerald-600">€</span>
                    </div>
                    <div>
                      <p className="font-semibold">Euro (EUR)</p>
                      <p className="text-sm text-slate-500">European Union</p>
                    </div>
                    {formData.currency === 'EUR' && (
                      <CheckCircle className="w-5 h-5 text-orange-500 ml-auto" />
                    )}
                  </label>
                </div>
                <p className="text-sm text-slate-500">
                  All prices in your menu and orders will be displayed in this currency.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Settings */}
          {formData.delivery_enabled && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-orange-500" />
                  Delivery Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="delivery_radius_km">Delivery Radius (km)</Label>
                    <Input
                      id="delivery_radius_km"
                      name="delivery_radius_km"
                      type="number"
                      step="0.5"
                      value={formData.delivery_radius_km}
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="minimum_order">Minimum Order ({formData.currency === 'EUR' ? '€' : '£'})</Label>
                    <Input
                      id="minimum_order"
                      name="minimum_order"
                      type="number"
                      step="0.50"
                      value={formData.minimum_order}
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="delivery_fee">Delivery Fee ({formData.currency === 'EUR' ? '€' : '£'})</Label>
                    <Input
                      id="delivery_fee"
                      name="delivery_fee"
                      type="number"
                      step="0.10"
                      value={formData.delivery_fee}
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Price Adjustments by Order Type */}
          {(formData.delivery_enabled || formData.takeaway_enabled) && (
            <Card data-testid="price-adjustments-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-orange-500" />
                  Global Price Adjustments
                </CardTitle>
                <CardDescription>
                  Adjust menu prices automatically based on order type. For example, add 10% to cover delivery commission costs.
                  Negative values will reduce prices.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Info Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">How it works:</p>
                      <ul className="list-disc ml-4 space-y-1">
                        <li>These adjustments apply to ALL menu items automatically</li>
                        <li>Customers see the adjusted prices when ordering</li>
                        <li>Original prices in your menu remain unchanged</li>
                        <li>Use positive values to increase prices, negative to decrease</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Delivery Price Adjustment */}
                  {formData.delivery_enabled && (
                    <div className="p-4 border rounded-lg bg-slate-50" data-testid="delivery-adjustment">
                      <div className="flex items-center gap-2 mb-3">
                        <Truck className="w-5 h-5 text-orange-500" />
                        <Label className="text-base font-semibold">Delivery Orders</Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Input
                          id="price_adjustment_delivery"
                          name="price_adjustment_delivery"
                          type="number"
                          step="0.5"
                          min="-50"
                          max="100"
                          value={formData.price_adjustment_delivery}
                          onChange={handleChange}
                          className="w-24"
                          data-testid="delivery-adjustment-input"
                        />
                        <span className="text-slate-600 font-medium">%</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        {formData.price_adjustment_delivery > 0 
                          ? `All delivery prices will be ${formData.price_adjustment_delivery}% higher`
                          : formData.price_adjustment_delivery < 0
                            ? `All delivery prices will be ${Math.abs(formData.price_adjustment_delivery)}% lower`
                            : 'No adjustment (same as menu price)'}
                      </p>
                      {/* Example calculation */}
                      {formData.price_adjustment_delivery !== 0 && (
                        <div className="mt-3 p-2 bg-white rounded border text-xs">
                          <span className="text-slate-500">Example: </span>
                          <span className="font-medium">€10.00 item → €{(10 * (1 + formData.price_adjustment_delivery / 100)).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Takeaway Price Adjustment */}
                  {formData.takeaway_enabled && (
                    <div className="p-4 border rounded-lg bg-slate-50" data-testid="takeaway-adjustment">
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="w-5 h-5 text-green-500" />
                        <Label className="text-base font-semibold">Takeaway Orders</Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Input
                          id="price_adjustment_takeaway"
                          name="price_adjustment_takeaway"
                          type="number"
                          step="0.5"
                          min="-50"
                          max="100"
                          value={formData.price_adjustment_takeaway}
                          onChange={handleChange}
                          className="w-24"
                          data-testid="takeaway-adjustment-input"
                        />
                        <span className="text-slate-600 font-medium">%</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        {formData.price_adjustment_takeaway > 0 
                          ? `All takeaway prices will be ${formData.price_adjustment_takeaway}% higher`
                          : formData.price_adjustment_takeaway < 0
                            ? `All takeaway prices will be ${Math.abs(formData.price_adjustment_takeaway)}% lower`
                            : 'No adjustment (same as menu price)'}
                      </p>
                      {/* Example calculation */}
                      {formData.price_adjustment_takeaway !== 0 && (
                        <div className="mt-3 p-2 bg-white rounded border text-xs">
                          <span className="text-slate-500">Example: </span>
                          <span className="font-medium">€10.00 item → €{(10 * (1 + formData.price_adjustment_takeaway / 100)).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Note about dine-in */}
                <p className="text-xs text-slate-400">
                  Note: Dine-in (table ordering) always uses the original menu prices with no adjustment.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Opening Hours */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                Opening Hours
              </CardTitle>
              <p className="text-sm text-slate-500">
                Add multiple time slots per day for lunch breaks (e.g., 10:00-14:00 and 16:00-23:00)
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {days.map((day) => (
                  <div key={day} className="border-b border-slate-100 pb-4 last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="font-medium capitalize w-24">{day}</span>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formData.opening_hours[day]?.closed || false}
                            onChange={() => toggleDayClosed(day)}
                            className="rounded border-slate-300"
                          />
                          <span className="text-slate-500">Closed</span>
                        </label>
                      </div>
                      {!formData.opening_hours[day]?.closed && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => addTimeSlot(day)}
                          className="text-orange-500 hover:text-orange-600"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Slot
                        </Button>
                      )}
                    </div>
                    
                    {!formData.opening_hours[day]?.closed && (
                      <div className="space-y-2 ml-4">
                        {(formData.opening_hours[day]?.slots || []).map((slot, slotIndex) => (
                          <div key={slotIndex} className="flex items-center gap-2">
                            <span className="text-xs text-slate-400 w-16">
                              {slotIndex === 0 ? 'Morning' : slotIndex === 1 ? 'Afternoon' : `Slot ${slotIndex + 1}`}
                            </span>
                            <Input
                              type="time"
                              value={slot.open || '09:00'}
                              onChange={(e) => handleHoursChange(day, slotIndex, 'open', e.target.value)}
                              className="w-32"
                            />
                            <span className="text-slate-400">to</span>
                            <Input
                              type="time"
                              value={slot.close || '22:00'}
                              onChange={(e) => handleHoursChange(day, slotIndex, 'close', e.target.value)}
                              className="w-32"
                            />
                            {(formData.opening_hours[day]?.slots || []).length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeTimeSlot(day, slotIndex)}
                                className="text-red-500 hover:text-red-600 h-8 w-8"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* POS & Device Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5 text-orange-500" />
                POS & Device Settings
              </CardTitle>
              <CardDescription>
                Configure this device's role in your restaurant network
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Primary POS Toggle */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-500 rounded-full">
                    <Server className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Primary POS Device</p>
                    <p className="text-sm text-slate-600">
                      When enabled, this device becomes the local network host during offline mode
                    </p>
                  </div>
                </div>
                <Switch
                  checked={localStorage.getItem('isPrimaryPOS') === 'true'}
                  onCheckedChange={(checked) => {
                    localStorage.setItem('isPrimaryPOS', checked ? 'true' : 'false');
                    // Also set a device ID if not exists
                    if (!localStorage.getItem('deviceId')) {
                      localStorage.setItem('deviceId', `pos-${Date.now()}`);
                    }
                    toast.success(checked 
                      ? 'This device is now the Primary POS' 
                      : 'Primary POS mode disabled'
                    );
                  }}
                  data-testid="primary-pos-toggle"
                />
              </div>

              {/* Device Info */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Label className="text-slate-500 text-xs uppercase tracking-wide">Device ID</Label>
                  <p className="font-mono text-sm mt-1">
                    {localStorage.getItem('deviceId') || 'Not set'}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Label className="text-slate-500 text-xs uppercase tracking-wide">Local Sync Port</Label>
                  <p className="font-mono text-sm mt-1">9200</p>
                </div>
              </div>

              {/* Host IP Configuration */}
              <div className="space-y-2">
                <Label>Main POS IP Address (for waiter devices)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., 192.168.1.100"
                    defaultValue={localStorage.getItem('localSync_hostIP') || ''}
                    onChange={(e) => {
                      localStorage.setItem('localSync_hostIP', e.target.value);
                    }}
                    className="font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const ip = localStorage.getItem('localSync_hostIP');
                      if (ip) {
                        toast.success(`Host IP saved: ${ip}`);
                      } else {
                        toast.warning('Enter an IP address first');
                      }
                    }}
                  >
                    Save IP
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Enter the IP address of your main POS computer. Waiter tablets will use this to connect when offline.
                </p>
              </div>

              {/* Offline Sync Info */}
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="flex items-start gap-3">
                  <Wifi className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">How Offline Sync Works</p>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                      <li>When internet goes down, the Primary POS becomes the local server</li>
                      <li>Waiter tablets automatically connect to the Primary POS via local network</li>
                      <li>Orders sync between all devices locally</li>
                      <li>When internet returns, all orders automatically sync to the cloud</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-settings">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
